/**
 * Script editor — Phase L4b CM6 migration.
 *
 * CodeMirror 6 + `@valtown/codemirror-ts` for TypeScript-aware autocomplete,
 * hover docs, and diagnostics against `script-sdk.d.ts`. The runtime stays
 * plain JS — the editor is the only thing that "knows" TS, so admins write
 * the same code they always have.
 *
 * Public signature is unchanged from the prior `react-simple-code-editor`
 * wrapper; new props are additive:
 *
 *   ScriptEditor({
 *     value, onChange, placeholder, minHeight = 160, language = 'javascript',
 *     categoryHint,      // 'home' | 'case' | 'task' | 'add_case' | 'add_task'
 *     caseTypeSchema,    // { fields: [{ name, type, ... }] } — for custom-fields completion (Phase E)
 *     onRunPreview,      // (currentValue) => void                    (Phase H)
 *   })
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { EditorState, Compartment } from '@codemirror/state';
import {
  EditorView, keymap, lineNumbers, highlightActiveLineGutter, highlightSpecialChars,
  drawSelection, dropCursor, rectangularSelection, crosshairCursor,
  highlightActiveLine, placeholder as placeholderExt,
} from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import {
  foldGutter, foldKeymap, indentOnInput, syntaxHighlighting,
  defaultHighlightStyle, bracketMatching, syntaxTree,
} from '@codemirror/language';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import {
  autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap,
  startCompletion,
} from '@codemirror/autocomplete';
import { lintKeymap, linter } from '@codemirror/lint';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark } from '@codemirror/theme-one-dark';

import {
  getTsKit, nextEditorId, pathsForEditor, headerForCategory,
} from './tsEnv';
import { BUILTIN_TOPICS } from './eventBus';
import { snippetCompletionSource } from './scriptSnippets';

// ── Forbidden-globals linter ──────────────────────────────────────────────
// The sandbox iframe doesn't expose these, but admins coming from regular
// browser JS may reach for them. Surface a hard error at edit time so they
// don't ship code that looks fine but throws at runtime.
const FORBIDDEN_GLOBALS = new Set([
  'window', 'document', 'localStorage', 'sessionStorage', 'eval',
]);

function forbiddenGlobalsLinter() {
  return linter((view) => {
    const diagnostics = [];
    const tree = syntaxTree(view.state);
    tree.iterate({
      enter(node) {
        if (node.name !== 'VariableName') return;
        const text = view.state.sliceDoc(node.from, node.to);
        if (FORBIDDEN_GLOBALS.has(text)) {
          diagnostics.push({
            from: node.from,
            to: node.to,
            severity: 'error',
            message: `'${text}' is not available inside the sandbox — use ctx.fetch / ctx.log / ctx.bus instead.`,
          });
        }
      },
    });
    return diagnostics;
  });
}

// ── Lightweight completion sources (no TS dependency) ─────────────────────

const TOPIC_OPTIONS = BUILTIN_TOPICS.map((t) => ({ label: t, type: 'enum' }));

/**
 * Suggest BUILTIN_TOPICS when the cursor is inside a string literal that's
 * the first arg of a `publish(...)` / `subscribe(...)` call. Pragmatic regex
 * detection — fast and doesn't fight the JS AST.
 */
function topicCompletionSource(context) {
  const word = context.matchBefore(/['"`][\w.-]*$/);
  if (!word) return null;
  const before = context.state.sliceDoc(Math.max(0, word.from - 32), word.from);
  if (!/\b(publish|subscribe)\s*\(\s*$/.test(before)) return null;
  return {
    from: word.from + 1,
    to: word.to,
    options: TOPIC_OPTIONS,
  };
}

/**
 * Suggest the active case type's custom field names when the cursor is
 * positioned after `ctx.case?.custom_fields?.` (with or without optional chain).
 * Reads the schema via a ref so prop updates take effect without rebuilding
 * the editor state.
 */
function makeCaseFieldsCompletionSource(schemaRef) {
  return (context) => {
    const fields = schemaRef.current?.fields;
    if (!Array.isArray(fields) || !fields.length) return null;
    const word = context.matchBefore(/[\w$]*$/);
    if (!word) return null;
    const before = context.state.sliceDoc(Math.max(0, word.from - 64), word.from);
    if (!/\bctx\??\.case\??\.custom_fields\??\.\s*$/.test(before)) return null;
    return {
      from: word.from,
      to: word.to,
      options: fields.map((f) => ({
        label: f.name || f.id || '',
        type: 'property',
        detail: f.type || '',
        info: f.label || '',
      })).filter((o) => o.label),
    };
  };
}

const HINT_LINES = [
  'payload     // value coming in from the wire (or null if invoked directly)',
  'ctx.case    // current case (null on Home)        ctx.work_item / ctx.draft',
  'ctx.bus.publish(topic, payload)                   ctx.invokeAction(widgetId, action, payload)',
  'await ctx.fetch(url, { method, headers, body })   // round-trips through /script-fetch',
  'helpers.formatDate / formatMoney / template / diff / slugify',
];

const DEFAULT_PLACEHOLDER = '// JavaScript — runs inside an iframe sandbox';

// Theme is built dynamically so we can swap between fixed-height (inline) and
// flex-fill (expanded / forceFillHeight) modes via a CM6 Compartment without
// reconstructing the editor.
function makeTheme(minHeightPx, fillHeight) {
  return EditorView.theme({
    '&': fillHeight
      ? { fontSize: '13px', height: '100%' }
      : { fontSize: '13px', minHeight: `${minHeightPx}px`, maxHeight: '400px' },
    '&.cm-focused': { outline: 'none' },
    '.cm-scroller': {
      fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', 'Monaco', monospace",
      lineHeight: '1.6',
    },
    '.cm-content': { padding: '10px 0' },
    '.cm-gutters': { backgroundColor: '#1e1e2e', borderRight: '1px solid #2a2a3a' },
  });
}

export default function ScriptEditor({
  value,
  onChange,
  placeholder,
  minHeight = 160,
  // language is reserved for future expansion; only 'javascript' is wired.
  // eslint-disable-next-line no-unused-vars
  language = 'javascript',
  categoryHint,
  caseTypeSchema,
  onRunPreview,
  // ⤢ button + Esc-toggle modal. Off when a parent (e.g. ScriptEditorWithPreview)
  // is driving expansion at its own level so editor + preview travel together.
  expandable = true,
  // Force the host to flex-fill its container instead of using minHeight/maxHeight.
  // Set by parent wrappers that are themselves rendered inside an expanded modal.
  forceFillHeight = false,
}) {
  const hostRef = useRef(null);
  const viewRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onRunPreviewRef = useRef(onRunPreview);
  onRunPreviewRef.current = onRunPreview;
  // Schema is read from a ref so prop changes flow into the completion source
  // without re-mounting the editor.
  const schemaRef = useRef(caseTypeSchema);
  schemaRef.current = caseTypeSchema;
  const caseFieldsSourceRef = useRef(null);
  if (caseFieldsSourceRef.current === null) {
    caseFieldsSourceRef.current = makeCaseFieldsCompletionSource(schemaRef);
  }

  const [expanded, setExpanded] = useState(false);
  const useFlex = expanded || forceFillHeight;

  // Stable per-editor id — keeps virtual TS file paths unique.
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = nextEditorId();
  const id = idRef.current;
  const { headerPath, scriptPath } = useMemo(() => pathsForEditor(id), [id]);

  // Compartments let us swap in TS extensions once the env is ready, without
  // reconstructing the whole editor.
  const tsCompartmentRef = useRef(null);
  const completionCompartmentRef = useRef(null);
  const themeCompartmentRef = useRef(null);
  if (tsCompartmentRef.current === null) tsCompartmentRef.current = new Compartment();
  if (completionCompartmentRef.current === null) completionCompartmentRef.current = new Compartment();
  if (themeCompartmentRef.current === null) themeCompartmentRef.current = new Compartment();

  useEffect(() => {
    if (!hostRef.current) return undefined;

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged && onChangeRef.current) {
        onChangeRef.current(update.state.doc.toString());
      }
    });

    const state = EditorState.create({
      doc: value || '',
      extensions: [
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightSpecialChars(),
        history(),
        foldGutter(),
        drawSelection(),
        dropCursor(),
        EditorState.allowMultipleSelections.of(true),
        indentOnInput(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        bracketMatching(),
        closeBrackets(),
        completionCompartmentRef.current.of(autocompletion({
          override: [
            topicCompletionSource,
            caseFieldsSourceRef.current,
            snippetCompletionSource,
          ],
        })),
        forbiddenGlobalsLinter(),
        rectangularSelection(),
        crosshairCursor(),
        highlightActiveLine(),
        highlightSelectionMatches(),
        keymap.of([
          {
            key: 'Mod-j',
            run: startCompletion,
          },
          {
            key: 'Shift-Mod-Enter',
            run: (view) => {
              if (!onRunPreviewRef.current) return false;
              onRunPreviewRef.current(view.state.doc.toString());
              return true;
            },
          },
          ...closeBracketsKeymap,
          ...defaultKeymap,
          ...searchKeymap,
          ...historyKeymap,
          ...foldKeymap,
          ...completionKeymap,
          ...lintKeymap,
          indentWithTab,
        ]),
        javascript({ jsx: false, typescript: false }),
        oneDark,
        themeCompartmentRef.current.of(makeTheme(minHeight, useFlex)),
        placeholderExt(placeholder || DEFAULT_PLACEHOLDER),
        // Empty until tsEnv resolves; reconfigured below.
        tsCompartmentRef.current.of([]),
        updateListener,
      ],
    });

    const view = new EditorView({ state, parent: hostRef.current });
    viewRef.current = view;

    // Async-load the TS env in the background. Editing works without it;
    // when it lands we hot-swap in autocomplete + lint + hover.
    let cancelled = false;
    getTsKit().then(({ env, valtown }) => {
      if (cancelled || !viewRef.current) return;
      const initialDoc = viewRef.current.state.doc.toString();
      env.createFile(headerPath, headerForCategory(categoryHint));
      env.createFile(scriptPath, initialDoc);
      viewRef.current.dispatch({
        effects: [
          completionCompartmentRef.current.reconfigure(
            autocompletion({
              override: [
                topicCompletionSource,
                caseFieldsSourceRef.current,
                snippetCompletionSource,
                valtown.tsAutocomplete(),
              ],
            }),
          ),
          tsCompartmentRef.current.reconfigure([
            valtown.tsFacet.of({ path: scriptPath, env }),
            valtown.tsSync(),
            valtown.tsHover(),
            valtown.tsLinter(),
          ]),
        ],
      });
    }).catch(() => {
      // Plain-JS fallback is fine — base extensions still give syntax/folding/etc.
    });

    return () => {
      cancelled = true;
      // Best-effort cleanup — env may not have resolved yet.
      getTsKit().then(({ env }) => {
        try { env.deleteFile(headerPath); } catch (_) { /* swallow */ }
        try { env.deleteFile(scriptPath); } catch (_) { /* swallow */ }
      }).catch(() => { /* swallow */ });
      view.destroy();
      viewRef.current = null;
    };
    // Mount once — value/placeholder/minHeight/categoryHint reconciled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // External `value` changes (e.g. layout switch) — overwrite the doc.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const cur = view.state.doc.toString();
    const next = value || '';
    if (cur !== next) {
      view.dispatch({ changes: { from: 0, to: cur.length, insert: next } });
    }
  }, [value]);

  // Re-stamp the header when category changes — narrows `ctx` accordingly.
  useEffect(() => {
    let cancelled = false;
    getTsKit().then(({ env }) => {
      if (cancelled) return;
      try { env.updateFile(headerPath, headerForCategory(categoryHint)); } catch (_) { /* swallow */ }
    }).catch(() => { /* swallow */ });
    return () => { cancelled = true; };
  }, [categoryHint, headerPath]);

  // Reconfigure CM6 theme when the host's sizing mode flips (inline vs flex-fill).
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: themeCompartmentRef.current.reconfigure(makeTheme(minHeight, useFlex)),
    });
  }, [useFlex, minHeight]);

  // Esc closes the internally-driven expanded modal.
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const wrapStyle = expanded
    ? {
      position: 'fixed',
      top: '4vh', left: '4vw', right: '4vw', bottom: '4vh',
      zIndex: 100,
      background: 'var(--bg-secondary, #1e1e2e)',
      border: '1px solid var(--border-color)',
      borderRadius: 8,
      padding: 12,
      display: 'flex', flexDirection: 'column', gap: 8,
      boxShadow: '0 12px 36px rgba(0,0,0,0.45)',
    }
    : (forceFillHeight
      ? { display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minHeight: 0 }
      : { display: 'flex', flexDirection: 'column', gap: 6 });

  const hostWrapStyle = useFlex
    ? { position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
    : { position: 'relative' };

  const hostStyle = useFlex
    ? {
      flex: 1, minHeight: 0,
      border: '1px solid var(--border-color)',
      borderRadius: 6,
      background: '#1e1e2e',
      overflow: 'hidden',
    }
    : {
      border: '1px solid var(--border-color)',
      borderRadius: 6,
      background: '#1e1e2e',
      minHeight,
      maxHeight: 400,
      overflow: 'hidden',
    };

  return (
    <>
      {expanded && (
        <div
          onMouseDown={() => setExpanded(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 99, background: 'rgba(0, 0, 0, 0.55)' }}
        />
      )}
      <div style={wrapStyle}>
        {expanded && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary, #aaa)' }}>JavaScript · Esc to close</span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              title="Collapse"
              aria-label="Collapse editor"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'var(--bg-tertiary, #313244)',
                color: 'var(--text-primary, #cdd6f4)',
                border: '1px solid var(--border-color)',
                borderRadius: 4,
                padding: '4px 10px',
                fontSize: 11, cursor: 'pointer',
              }}
            >
              ⤡ Collapse
            </button>
          </div>
        )}
        <details style={{ background: 'var(--bg-tertiary, #313244)', borderRadius: 4, padding: '4px 8px' }}>
          <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--text-tertiary, #888)' }}>Available in scope</summary>
          <pre style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--text-secondary, #aaa)', whiteSpace: 'pre-wrap', fontFamily: "'Fira Code', monospace" }}>
{HINT_LINES.join('\n')}
          </pre>
        </details>
        <div style={hostWrapStyle}>
          <div ref={hostRef} style={hostStyle} />
          {expandable && !expanded && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
              title="Expand editor"
              aria-label="Expand editor"
              style={{
                position: 'absolute', top: 6, right: 6, zIndex: 5,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26,
                background: 'rgba(19, 26, 38, 0.85)',
                color: 'var(--text-primary, #cdd6f4)',
                border: '1px solid var(--border-color)',
                borderRadius: 4,
                fontSize: 14, lineHeight: 1, cursor: 'pointer',
              }}
            >
              ⤢
            </button>
          )}
        </div>
      </div>
    </>
  );
}
